import React, { useState } from "react";
import { Check, ChevronDown, Download, FileImage, FileSpreadsheet, FileText, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  downloadCSV,
  generateComprehensiveCSV,
  generateDetailedPDF,
  generatePDF,
  generateSimpleCSV,
  type ReportData,
} from "@/lib/reportExport";

interface DownloadReportButtonProps {
  reportId: string;
  reportData: unknown;
}

export default function DownloadReportButton({ reportId, reportData }: DownloadReportButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const showSuccess = () => {
    setDownloadSuccess(true);
    setDownloadError(null);
    setTimeout(() => setDownloadSuccess(false), 2000);
  };

  const showError = (message: string) => {
    setDownloadError(message);
    setTimeout(() => setDownloadError(null), 5000);
  };

  const hasValidReport = reportData != null && typeof reportData === "object" && (reportData as ReportData).id != null;

  const handleComprehensiveCSV = async () => {
    if (!hasValidReport) {
      showError("Report data is not available. Please wait for the report to load.");
      return;
    }
    try {
      setDownloadProgress(0);
      setIsDownloading(true);
      setDownloadError(null);
      const csvContent = generateComprehensiveCSV(reportData as ReportData);
      downloadCSV(csvContent, `report-comprehensive-${reportId}.csv`);
      setDownloadProgress(100);
      showSuccess();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to generate CSV.");
      console.error("Error generating CSV:", error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handleSimpleCSV = async () => {
    if (!hasValidReport) {
      showError("Report data is not available. Please wait for the report to load.");
      return;
    }
    try {
      setDownloadProgress(0);
      setIsDownloading(true);
      setDownloadError(null);
      const csvContent = generateSimpleCSV((reportData as ReportData).flags ?? []);
      downloadCSV(csvContent, `report-flags-${reportId}.csv`);
      setDownloadProgress(100);
      showSuccess();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to generate CSV.");
      console.error("Error generating CSV:", error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handleVisualPDF = async () => {
    try {
      setIsDownloading(true);
      setDownloadProgress(0);
      setDownloadError(null);
      await generatePDF("report-content", `report-visual-${reportId}.pdf`, {
        onProgress: (progress) => setDownloadProgress(progress),
        quality: 1,
      });
      setDownloadProgress(100);
      showSuccess();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to generate PDF. Please try again.");
      console.error("Error generating PDF:", error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handleDetailedPDF = async () => {
    if (!hasValidReport) {
      showError("Report data is not available. Please wait for the report to load.");
      return;
    }
    try {
      setDownloadProgress(0);
      setIsDownloading(true);
      setDownloadError(null);
      await generateDetailedPDF(reportData as ReportData, `report-detailed-${reportId}.pdf`);
      setDownloadProgress(100);
      showSuccess();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to generate PDF. Please try again.");
      console.error("Error generating PDF:", error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="secondary"
            disabled={isDownloading}
            className="gap-2 hover:shadow-md transition-all border"
          >
            {isDownloading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {downloadProgress > 0 ? `${downloadProgress}%` : "Processing..."}
              </>
            ) : downloadSuccess ? (
              <>
                <Check className="h-4 w-4 text-green-600" />
                <span className="text-green-600 font-semibold">Downloaded!</span>
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export Report
                <ChevronDown className="h-3 w-3 ml-1" />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>Export Format</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleVisualPDF} disabled={isDownloading}>
          <FileImage className="mr-2 h-4 w-4" />
          <div className="flex flex-col">
            <span className="font-medium">Visual PDF</span>
            <span className="text-xs text-muted-foreground">Same layout as webpage</span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={handleDetailedPDF} disabled={isDownloading}>
          <FileText className="mr-2 h-4 w-4" />
          <div className="flex flex-col">
            <span className="font-medium">Detailed PDF</span>
            <span className="text-xs text-muted-foreground">Text-based report</span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleComprehensiveCSV} disabled={isDownloading}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          <div className="flex flex-col">
            <span className="font-medium">Full CSV Report</span>
            <span className="text-xs text-muted-foreground">All data sections</span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={handleSimpleCSV} disabled={isDownloading}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          <div className="flex flex-col">
            <span className="font-medium">Flags CSV</span>
            <span className="text-xs text-muted-foreground">Challenges only</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    {downloadError && (
      <p className="text-xs text-destructive font-medium" role="alert">
        {downloadError}
      </p>
    )}
    </div>
  );
}


